import { onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue';
import { forgetSockets, initializeSockets, socketSessions } from './socket.instance';
import type { SocketSession } from './socket-session';
import { addSocketAuthRoutines } from '@platform/shared/rpc/socket/sock-auth';
import config from '@platform/shared/config';
import { captureException } from '@sentry/browser';

const notifyError = <T>(err: T): T => {
    captureException(err);
    console.warn(err);
    // Notify.create({
    //     type: 'error',
    //     position: 'top-right',
    //     color: 'red',
    //     textColor: 'white',
    //     message: error['message'] || 'Неизвестная ошибка',
    //     caption: `Ошибка${error.name ? ` (${error.name})` : ''}`,
    // });

    return err as any;
};

export const sockApi = {
    _: {
        getSocket: (namespace: keyof typeof socketSessions = 'default') => socketSessions[namespace],
        getRawSocket: (namespace: keyof typeof socketSessions = 'default') => socketSessions[namespace].raw,
        setAuthData: (auth: { sysParams: any; token: string } | null) => {
            if (auth) {
                initializeSockets({
                    auth: {
                        sysOrgId: auth.sysParams.sysOrgId,
                        token: auth.token,
                        sysServiceId: config.serviceId,
                    },
                });
            } else {
                forgetSockets();
            }
        },
        timedOutEmit: <T = any>(session: SocketSession, event: string, args: any[], timeout = 15000) => {
            return session
                .emitWithAck<T>(event, args, { timeout, policy: 'wait-auth' })
                .catch((error) => Promise.reject(notifyError(error)));
        },
        promised: <T = any>(session: SocketSession, event: string, args: any[], timeout = 5000, volatile = false) => {
            return session
                .emitWithAck<T>(event, args, {
                    timeout,
                    volatile,
                    policy: volatile ? 'reject-if-unauthorized' : 'wait-auth',
                })
                .catch((error) => Promise.reject(notifyError(error)));
        },
        useSocketConnectionState: function (namespace: keyof typeof socketSessions = 'default') {
            const socket = socketSessions[namespace];
            const isConnected = ref(true);
            const isAuthorized = ref(false);
            const socketState = ref(socket.state);
            const connectedHandler = () => {
                isConnected.value = true;
            };
            const disconnectedHandler = () => {
                isConnected.value = false;
                isAuthorized.value = false;
            };
            const authorizedHandler = () => {
                isAuthorized.value = true;
                isConnected.value = socket.connected;
            };
            const unauthorizedHandler = () => {
                isAuthorized.value = false;
            };
            const stateHandler = (state: typeof socketState.value) => {
                socketState.value = state;
            };
            onBeforeMount(() => {
                socket
                    .on('connect', connectedHandler)
                    .on('disconnect', disconnectedHandler)
                    .on('connect_error', disconnectedHandler)
                    .on('error', disconnectedHandler)
                    .on('authorized', authorizedHandler)
                    .on('unauthorized', unauthorizedHandler)
                    .on('session_state', stateHandler);
            });

            onBeforeUnmount(() => {
                socket
                    .off('connect', connectedHandler)
                    .off('disconnect', disconnectedHandler)
                    .off('connect_error', disconnectedHandler)
                    .off('error', disconnectedHandler)
                    .off('authorized', authorizedHandler)
                    .off('unauthorized', unauthorizedHandler)
                    .off('session_state', stateHandler);
            });
            onMounted(() => {
                isConnected.value = socket.connected;
                isAuthorized.value = socket.authorized;
                socketState.value = socket.state;
            });
            setTimeout(() => {
                isConnected.value = socket.connected;
                isAuthorized.value = socket.authorized;
                socketState.value = socket.state;
            }, 3000);
            return { isSockConnected: isConnected, isSockAuthorized: isAuthorized, socketState };
        },
    },
    rpc: (endpoint: string, params?: any, timeout = 15000): PromiseLike<any> => {
        return sockApi._.timedOutEmit(socketSessions.default, endpoint, [{ ...params }], timeout);
    },
};

addSocketAuthRoutines(sockApi);
